import { useBPLogger } from '@/composables/business-process-logger';
import {
  CreateDraftDto,
  CreateDraftLineDto,
  DraftInitInfoDto,
  DraftModel,
  OrderSummaryDto,
} from '@onrunning/backstage-models';
import { ValidatorResult } from '../models/validation-result.model';
import { useOrderHeadersStore } from '../stores/order-headers';
import { CrudService } from './crud.service';

export class DraftsService {
  private crudService: CrudService<any>;
  private readonly setRefreshHeaders: () => void;
  private submitOrderBPLog;
  private addToCartBPLog;

  constructor() {
    const { setRefreshHeaders } = useOrderHeadersStore();
    this.setRefreshHeaders = setRefreshHeaders;
    this.crudService = new CrudService<any>('api/v2/drafts');
    const { submitOrderBPLog, addToCartBPLog } = useBPLogger();
    this.submitOrderBPLog = submitOrderBPLog;
    this.addToCartBPLog = addToCartBPLog;
  }

  public get(id: string, accountId: string, withRelatedDrafts = false): Promise<ValidatorResult<DraftModel>> {
    return this.crudService.get(id, { params: { accountId, withRelatedDrafts } });
  }

  public async update(draft: DraftModel, accountId: string): Promise<ValidatorResult<DraftModel>> {
    const result = await this.crudService.put(draft.id, draft, { params: { accountId } });

    this.setRefreshHeaders();

    return result;
  }

  public async remove(id: string, accountId: string): Promise<ValidatorResult<DraftModel>> {
    const result = await this.crudService.remove(id, { params: { accountId } });

    this.setRefreshHeaders();

    return result;
  }

  public async submit(draft: DraftModel, accountId: string): Promise<ValidatorResult<DraftModel>> {
    const result = await this.crudService.do('put', `submit/${draft.id}?${this.getAccountUrlParam(accountId)}`, draft);

    const logData = {
      order_number: result?.model?.orderNumber,
      subflow: 'order_duplication',
    };

    if (result.isValid()) {
      console.log(this.submitOrderBPLog(true, logData));
    } else {
      console.log(
        this.submitOrderBPLog(false, {
          ...logData,
          error_message: result?.errors?.join(','),
        }),
      );
    }
    this.setRefreshHeaders();

    return result;
  }

  public getPrices(draftId: string, accountId: string, productId?: string): Promise<ValidatorResult<DraftModel>> {
    const line = productId ? `/lines/${productId}` : '';

    return this.crudService.do('get', `${draftId}/prices${line}?${this.getAccountUrlParam(accountId)}`);
  }

  public async getInitInfo(accountId: string): Promise<ValidatorResult<DraftInitInfoDto>> {
    return this.crudService.doWithCustomType<DraftInitInfoDto>(
      'get',
      `create/init-info?${this.getAccountUrlParam(accountId)}`,
    );
  }

  public getOrderSummary(accountId: string, orderNumbers: string[]): Promise<ValidatorResult<OrderSummaryDto[]>> {
    const queryParams = new URLSearchParams();

    queryParams.set('accountId', accountId);

    return this.crudService.do('post', `summary?${queryParams}`, { orderNumbers });
  }

  public async create(
    createDraftDto: CreateDraftDto,
    accountId: string,
  ): Promise<ValidatorResult<CreateDraftDto & { id?: string; orderNumber?: string }>> {
    const result = await this.crudService.post(createDraftDto, { params: { accountId } });

    const logData = {
      order_number: result?.model?.orderNumber,
      draft_id: result?.model?.id,
      item_numbers: result?.model?.lines?.map((line: CreateDraftLineDto) => line.itemNumber),
      subflow: 'create_draft',
    };

    if (result.isValid()) {
      console.log(this.addToCartBPLog(true, logData));
    } else {
      const error_message = result?.errors?.join(',');
      console.log(this.addToCartBPLog(false, { ...logData, error_message }));
    }

    this.setRefreshHeaders();

    return result;
  }

  public async getSpecificOrders(accountId: string, orderNumbers: string[]): Promise<ValidatorResult<DraftModel[]>> {
    return await this.crudService.do('get', `selected-drafts?accountId=` + accountId + `&orderNumbers=` + orderNumbers);
  }

  private getAccountUrlParam(accountId: string) {
    const params = new URLSearchParams();

    params.set('accountId', accountId);

    return params;
  }
}
